import React, { useEffect } from "react";
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { sendSlackMessageTracked } from "./models/notifications";
import { logPageVisit } from "./models/firestoreService";
import reportWebVitals from './reportWebVitals';
import {
    BrowserRouter as Router,
    Route,
    Routes,
    Navigate,
    useLocation,
} from "react-router-dom";
import { HelmetProvider, Helmet } from 'react-helmet-async';

import MainHomeComponent from "./components/home/mainHomeComponent";
import About from "./components/about/about";
import Mission from "./components/mission/mission";
import MainMeetAStudentComponent from "./components/stories/MainMeetAStudentComponent";
import MainStudentLifeComponent from "./components/student-life/mainStudentLifeComponent";
import MainStudentLifeRoleComponent from "./components/student-life/mainStudentLifeRoleComponent";
import MainAdmissionsComponent from "./components/admissions/mainAdmissionsComponent";
import PrivacyPolicy from "./components/misc-pages/privacyPolicy";
import MainApplyComponent from "./components/apply/mainApplyComponent";
import MainDefinitions from "./components/educate/definitions/mainDefinitions";
import MainFounders from "./components/educate/founders/mainFounders";
import MainFinancialAidComponent from "./components/apply/scholarship-and-financial-aid/mainFinancialAidComponent";
import MainPrimaryIntensiveComponent from "./components/admissions/programs/primaryIntensive/mainPrimaryIntensiveComponent";
import MainTrainerComponent from "./components/educate/trainer/mainTrainerComponent";
import MainSecondaryIntensive from "./components/admissions/programs/secondaryIntensive/mainSecondaryIntensive";
import MainResidencyComponent from "./components/admissions/programs/residency/mainResidencyComponent";
import MainNextBigThing from "./components/stories/next-big-thing/mainNextBigThing";
import MainContactUsPage from "./components/contact/mainContactUsPage";
import MainFlyerComponent from "./components/flyer/mainFlyerComponent";
import PageNotFound from "./components/misc-pages/pageNotFound";
import MainParentPageComponent from "./components/stories/parent/mainParentPageComponent";
import MainStudentPageComponent from "./components/stories/student/mainStudentPageComponent";
import MainStudentNeedToKnowComponent from "./components/stories/student/mainStudentNeedToKnowComponent";
import MainPartnersPage from "./components/partners/mainPartnersPage";
import CeoDashboard from "./components/dashboard/ceoDashboard";

const getPageTitle = (pathname) => {
    if (pathname.endsWith("/")) {
        pathname = pathname.slice(0, -1);
    }
    if (pathname.startsWith("/student-life/")) {
        const roleSlug = pathname.replace("/student-life/", "");
        const roleTitles = { ceo: "CEO", cmo: "CMO", cto: "CTO", cpo: "CPO", coo: "COO", cgo: "CGO" };
        const title = roleTitles[roleSlug];
        return title ? `${title} — Life at Extern Academy` : "Student Life";
    }
    if (pathname.includes("/offer")) {
        // if (isHolidayFocus) {
        //     return currentHolidayLong
        // }
        return "Offer Page";
    }

    switch (pathname) {
        case "":
            return "Home";

        // About
        case "/about":
            return "About";

        // Mission & Philosophy
        case "/mission":
            return "Mission & Philosophy";

        // Meet a Student
        case "/stories/meet-a-student":
            return "Meet a Student";

        // Student Life
        case "/student-life":
            return "Student Life";

        // Admissions
        case "/admissions":
            return "Admissions";

        // 2027 Startup Sprint
        case "/admissions/2027-startup-sprint":
            return "2027 Startup Sprint";

        // Founder Track
        case "/admissions/founder-track":
            return "Founder Track";

        // Residency
        case "/admissions/residency":
            return "Residency";

        // Apply
        case "/apply":
            return "Apply";

        // Apply for Financial Aid
        case "/financial-aid":
            return "Apply for Financial Aid";

        // Startup Definitions
        case "/education/definitions":
            return "Startup Definitions";

        // Startup Trainer
        case "/education/trainer":
            return "Startup Trainer";

        // Startup Founders
        case "/education/founders":
            return "Startup Founders";

        // Parents Need to Know
        case "/stories/parent-need-to-know":
            return "Parents Need to Know";

        // Students Need to Know
        case "/stories/students-need-to-know":
            return "Students Need to Know";

        // Student Need to Know
        case "/stories/student-need-to-know":
            return "Student Need to Know";

        // Next Big Thing
        case "/stories/next-big-thing":
            return "Next Big Thing";

        // Contact Us
        case "/contact-us":
            return "Contact Us";

        // Partners
        case "/partners":
            return "Partners";

        // Privacy Policy
        case "/privacy":
            return "Privacy Policy";

        default:
            return "Not Found";
    }
};

// Component to update the page title (SEO)
const PageTitle = () => {
    const location = useLocation();
    const pageTitle = getPageTitle(location.pathname);
    return <Helmet><title>{pageTitle}</title></Helmet>;
};

// Component to track page views (internal logging + GA4)
const TrackPageViews = () => {
    const location = useLocation();
    useEffect(() => {
        const fullUrl = location.pathname + location.search;
        sendSlackMessageTracked(`Customer is visiting the ${fullUrl} page`);
        logPageVisit(fullUrl).catch(() => {});

        if (typeof window.gtag === "function") {
            window.gtag("event", "page_view", {
                page_path: fullUrl,
                page_title: document.title,
            });
        }
    }, [location]);
    return null;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <HelmetProvider>
            <Router>
                <PageTitle />
                <TrackPageViews />
                <Routes>
                    <Route path="/" element={<MainHomeComponent />} />
                    <Route path="/mission" element={<Mission />} />

                    {/* Admissions */}
                    <Route path="/admissions" element={<MainAdmissionsComponent />} />
                    <Route path="/admissions/2027-startup-sprint" element={<MainPrimaryIntensiveComponent />} />
                    <Route path="/admissions/2026-startup-sprint" element={<Navigate to="/admissions/2027-startup-sprint" replace />} />
                    <Route path="/admissions/intensive-june" element={<Navigate to="/admissions/2027-startup-sprint" replace />} />
                    <Route path="/sprint" element={<Navigate to="/admissions/2027-startup-sprint" replace />} />

                    <Route path="/admissions/founder-track" element={<MainSecondaryIntensive />} />
                    <Route path="/admissions/intensive-july" element={<Navigate to="/admissions/founder-track" replace />} />
                    <Route path="/founder-track" element={<Navigate to="/admissions/founder-track" replace />} />
                    <Route path="/ft" element={<Navigate to="/admissions/founder-track" replace />} />
                    <Route path="/admissions/residency" element={<MainResidencyComponent />} />

                    {/* Education pages */}
                    <Route path="/education/definitions" element={<MainDefinitions />} />
                    <Route path="/education/founders" element={<MainFounders />} />
                    <Route path="/education/trainer" element={<MainTrainerComponent />} />

                    {/* Applying pages */}
                    <Route path="/apply" element={<MainApplyComponent />} />
                    <Route path="/financial-aid" element={<MainFinancialAidComponent />} />

                    {/* Stories pages */}
                    <Route path="/stories/parent-need-to-know" element={<MainParentPageComponent />} />
                    <Route path="/stories/students-need-to-know" element={<MainStudentPageComponent />} />
                    <Route path="/stories/student-need-to-know" element={<MainStudentNeedToKnowComponent />} />
                    <Route path="/stories/next-big-thing" element={<MainNextBigThing />} />

                    {/* Flyer QR routing */}
                    <Route path="/flyer/:page" element={<MainFlyerComponent />} />

                    {/* Contact pages */}
                    <Route path="/contact-us" element={<MainContactUsPage />} />

                    {/* Partners */}
                    <Route path="/partners" element={<MainPartnersPage />} />

                    <Route path="/about" element={<About />} />
                    {/* <Route path="/stories/meet-a-student" element={<MainMeetAStudentComponent />} /> */}

                    {/* Student Life hub + role sub-pages */}
                    <Route path="/student-life" element={<MainStudentLifeComponent />} />
                    <Route path="/student-life/:role" element={<MainStudentLifeRoleComponent />} />

                    {/* CEO Dashboard — password protected */}
                    <Route path="/dashboard" element={<CeoDashboard />} />

                    <Route path="/privacy" element={<PrivacyPolicy />} />

                    <Route path="*" element={<PageNotFound />} />
                </Routes>
            </Router>
        </HelmetProvider>
    </React.StrictMode>
);

// If you want to measure performance
reportWebVitals();